import { isDeployChunkFailure } from "@/domains/shared/appUpdatePrompt/installChunkFailHardPath";
import { Button } from "@/components/ui/button";
import { getTranslation } from "@mivo/i18n";
import { useEffect } from "react";

export const LAZY_CHUNK_RELOAD_STORAGE_KEY = "mivo_lazy_chunk_reload";

/**
 * React.lazy failures throw during render, so they never hit the window
 * unhandledrejection bridge. One document reload per tab session recovers
 * WebKit empty-module / stale-chunk cases (BR-05, BR-10).
 */
export function tryReloadOnDeployChunkFailure(
  error: unknown,
  reload: () => void = () => {
    window.location.reload();
  },
): boolean {
  if (!isDeployChunkFailure(error)) {
    return false;
  }

  try {
    if (sessionStorage.getItem(LAZY_CHUNK_RELOAD_STORAGE_KEY) === "1") {
      return false;
    }

    sessionStorage.setItem(LAZY_CHUNK_RELOAD_STORAGE_KEY, "1");
  } catch {
    return false;
  }

  reload();
  return true;
}

const TRANSLATIONS = {
  title: {
    "pt-BR": "Algo deu errado",
    en: "Something went wrong",
    es: "Algo salió mal",
    de: "Etwas ist schief gelaufen",
  },
  description: {
    "pt-BR": "Ocorreu um erro inesperado. Tente recarregar a página.",
    en: "An unexpected error occurred. Try reloading the page.",
    es: "Ocurrió un error inesperado. Intenta recargar la página.",
    de: "Es ist ein unerwarteter Fehler aufgetreten. Versuchen Sie, die Seite neu zu laden.",
  },
  reload: {
    "pt-BR": "Recarregar",
    en: "Reload",
    es: "Recargar",
    de: "Neu laden",
  },
} as const;

export function ErrorFallback({ error }: { error?: unknown } = {}) {
  useEffect(() => {
    if (import.meta.env.DEV) {
      return;
    }

    tryReloadOnDeployChunkFailure(error);
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="text-center space-y-4 max-w-sm">
        <h1 className="text-xl font-semibold text-foreground">
          {getTranslation(TRANSLATIONS.title)}
        </h1>
        <p className="text-muted-foreground">
          {getTranslation(TRANSLATIONS.description)}
        </p>
        <Button
          type="button"
          onClick={() => window.location.reload()}
          className="min-h-[44px]"
        >
          {getTranslation(TRANSLATIONS.reload)}
        </Button>
      </div>
    </div>
  );
}
